// HubFeed — the main hub feed surface.
// Renders the filter/sort/search controls and the active view (Feed or Editorial).
// This is the entry-point component: import HubFeed from @/features/hub-feed.
import { useCallback, useEffect, useRef, useState } from "react";
import { ComposePost, type PostResponse } from "@/features/posting-engagement";
import {
  SortBar,
  SearchBox,
  DEFAULT_FEED_FILTERS,
} from "../FilterSortSearch";
import type { FeedFilters } from "../FilterSortSearch";
import { useFeed } from "../hooks/useFeed";
import { useAuthors } from "../useAuthors";
import { EditorialView } from "./EditorialView";
import { FeedView } from "./FeedView";
import { ViewToggle } from "./ViewToggle";

interface HubFeedProps {
  /** Optional external search query supplied by the app-shell topbar search. */
  query?: string;
  /** Called when a feed row / editorial card is activated. */
  onOpenPost?: (id: string) => void;
  /**
   * Called instead of onOpenPost when the activated card is a Training
   * resource's feed cross-post (post.training_resource_id is set) — routes
   * to the Training detail page rather than PostDetail.
   */
  onOpenTraining?: (resourceId: string) => void;
}

export function HubFeed({ query: externalQuery, onOpenPost, onOpenTraining }: HubFeedProps = {}) {
  const authors = useAuthors();
  const [filters, setFilters] = useState<FeedFilters>(DEFAULT_FEED_FILTERS);

  // The effective query prefers the app-shell topbar search when present,
  // falling back to the in-feed search box.
  const effectiveQuery = externalQuery?.trim() ? externalQuery : filters.query;

  // Merge filter/sort/search state into one param object
  const feedParams = {
    ...(filters.sort !== "trending" ? { sort: filters.sort } : {}),
    ...(effectiveQuery ? { q: effectiveQuery } : {}),
  };

  const { data, loading, error, view, setView, refetch } = useFeed(
    Object.keys(feedParams).length > 0 ? feedParams : undefined,
  );

  // Compose modal open/close state.
  const [composeOpen, setComposeOpen] = useState(false);
  const newPostBtnRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close the compose modal on Escape for keyboard dismissal.
  useEffect(() => {
    if (!composeOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setComposeOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [composeOpen]);

  // Move focus into the modal when it opens; restore to the trigger when it closes.
  useEffect(() => {
    if (composeOpen) {
      const focusable = modalRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    } else {
      newPostBtnRef.current?.focus();
    }
  }, [composeOpen]);

  // Tab / Shift+Tab focus trap while the modal is open.
  const handleModalKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [],
  );

  // One-shot "just posted" flag — drives a brief celebration flourish on the
  // new post's own card once it reappears in the refetched feed. Not
  // persisted anywhere; a generous fallback timeout clears it even if the
  // refetch is slow or prefers-reduced-motion means no animation ever fires.
  const [justPostedId, setJustPostedId] = useState<string | null>(null);
  useEffect(() => {
    if (!justPostedId) return;
    const t = setTimeout(() => setJustPostedId(null), 3000);
    return () => clearTimeout(t);
  }, [justPostedId]);

  // On successful post: close the modal, refresh the feed so the new post
  // appears, and flag it for the celebration animation.
  const handleComposeSuccess = useCallback(
    (post: PostResponse) => {
      setComposeOpen(false);
      setJustPostedId(post.id);
      refetch();
    },
    [refetch],
  );

  // A feed card whose post is a Training resource's feed cross-post routes
  // to the Training detail page instead of PostDetail.
  const handleOpen = useCallback(
    (id: string) => {
      const post = data?.items.find((p) => p.id === id);
      if (post?.training_resource_id && onOpenTraining) {
        onOpenTraining(post.training_resource_id);
      } else {
        onOpenPost?.(id);
      }
    },
    [data, onOpenPost, onOpenTraining],
  );

  return (
    <section className="hub-feed" aria-label="Hub feed">
      {/* Controls bar: sort on the left, view toggle + New Post on the right */}
      <div className="hub-feed__controls">
        {/* Sort toggle */}
        <SortBar filters={filters} onFiltersChange={setFilters} disabled={loading} />

        {/* Right-side action cluster */}
        <div className="hub-feed__controls-actions">
          {/* View toggle */}
          <ViewToggle view={view} onChange={setView} />

          {/* New post entry point */}
          <button
            ref={newPostBtnRef}
            type="button"
            className="hub-newpost"
            data-testid="hub-newpost"
            onClick={() => setComposeOpen(true)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Post
          </button>
        </div>
      </div>

      {/* Full-width search row beneath the control bar */}
      <SearchBox filters={filters} onFiltersChange={setFilters} disabled={loading} />

      {/* Content */}
      {loading && (
        <div className="hub-loading" role="status" aria-live="polite">
          Loading feed…
        </div>
      )}

      {error && !loading && (
        <div className="hub-error" role="alert">
          <strong>Could not load the feed.</strong>
          <p>{error.message}</p>
        </div>
      )}

      {data && !loading && (
        <>
          {view === "feed" ? (
            <FeedView
              posts={data.items}
              emptyState={data.empty_state}
              authors={authors}
              onOpen={handleOpen}
              justPostedId={justPostedId}
            />
          ) : (
            <EditorialView
              posts={data.items}
              emptyState={data.empty_state}
              onOpen={handleOpen}
            />
          )}
        </>
      )}

      {/* Compose post modal */}
      {composeOpen && (
        <div
          className="hub-modal-overlay"
          data-testid="hub-compose-modal"
          onClick={() => setComposeOpen(false)}
        >
          <div
            ref={modalRef}
            className="hub-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Create a new post"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleModalKeyDown}
          >
            <div className="hub-modal__head">
              <h2 className="hub-modal__title">New Post</h2>
              <button
                type="button"
                className="hub-modal__close"
                data-testid="hub-compose-close"
                aria-label="Close"
                onClick={() => setComposeOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <ComposePost onSuccess={handleComposeSuccess} />
          </div>
        </div>
      )}
    </section>
  );
}
